import { useEffect } from 'react';

/**
 * Google Publisher Tag (GPT) Initialization Configuration
 *
 * Configures GPT for optimal ad serving performance:
 * - Lazy loading: Fetches ads 5 viewports ahead, renders 2 viewports ahead
 * - Single request architecture: All ads requested in one network call
 * - Empty slot collapsing: Prevents layout shifts from empty ad slots
 */
const GPT_CONFIG = {
  lazyLoad: {
    fetchMarginPercent: 500, // Fetch ad 5 viewports before visible
    renderMarginPercent: 200, // Render ad 2 viewports before visible
    mobileScaling: 2.0, // Double the margins on mobile for slower connections
  },
} as const;

/**
 * Custom hook to initialize Google Publisher Tag (GPT) for ad serving.
 *
 * This hook should be called once at the app level to configure GPT
 * for all ad slots throughout the application.
 *
 * Features:
 * - Initializes googletag object if not already present
 * - Configures lazy loading for better performance
 * - Enables single request architecture (all ads in one request)
 * - Collapses empty ad slots to prevent layout shifts
 * - Enables GPT services
 *
 * @example
 * ```tsx
 * const App = () => {
 *   useGooglePublisherTag();
 *   return <YourApp />;
 * };
 * ```
 */
export const useGooglePublisherTag = (): void => {
  useEffect(() => {
    // Initialize googletag object if it doesn't exist
    // This creates a minimal stub that the GPT script will replace when it loads
    // The cmd array allows queuing commands before the script loads
    if (!window.googletag) {
      window.googletag = { cmd: [] } as Window['googletag'];
    }

    // Queue GPT configuration commands
    // These will execute when the GPT script loads
    window.googletag.cmd.push(() => {
      // Enable lazy loading for better performance
      // Ads are fetched 5 viewports ahead and rendered 2 viewports ahead
      window.googletag.pubads().enableLazyLoad(GPT_CONFIG.lazyLoad);

      // Enable single request architecture (all ads in one request)
      window.googletag.pubads().enableSingleRequest();

      // Collapse empty ad slots to prevent layout shifts
      window.googletag.pubads().collapseEmptyDivs();

      // Enable GPT services
      window.googletag.enableServices();
    });
  }, []);
};
